import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Capped at 3 to match the free plan limit; new detailers can add more after upgrading.
const DEFAULT_SERVICES = [
  { name:"Basic Wash",       description:"Exterior hand wash, windows, tire shine",           icon:"💧", duration:"1–1.5 hrs", pricing:{ sedan:40,  suv:55,  truck:60,  van:65  } },
  { name:"Interior Detail",  description:"Full vacuum, wipe down, leather conditioning",      icon:"✨", duration:"2–3 hrs",   pricing:{ sedan:80,  suv:100, truck:100, van:110 } },
  { name:"Full Detail",      description:"Complete interior + exterior detail package",       icon:"🏆", duration:"4–6 hrs",   pricing:{ sedan:150, suv:180, truck:185, van:200 } },
];

const css = `
  .signup-page { min-height:100vh; display:flex; align-items:center; justify-content:center; padding:24px; }
  .signup-card { background:var(--card); border:1px solid var(--border); border-radius:20px; padding:40px; max-width:480px; width:100%; }
  .signup-logo { font-family:var(--head); font-size:26px; font-weight:800; letter-spacing:2px; margin-bottom:24px; }
  .signup-logo span { color:var(--blue); }
  .step-bar { display:flex; align-items:center; margin-bottom:32px; }
  .step-pip { width:32px; height:32px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; border:2px solid var(--border); color:var(--muted); background:var(--card); transition:all .3s; flex-shrink:0; }
  .step-pip.active { border-color:var(--blue); background:var(--blue); color:#fff; }
  .step-pip.done   { border-color:var(--teal); background:var(--teal); color:#fff; }
  .step-line { flex:1; height:2px; background:var(--border); transition:background .3s; }
  .step-line.done { background:var(--teal); }
  .step-title { font-family:var(--head); font-size:22px; font-weight:800; letter-spacing:1px; margin-bottom:6px; }
  .step-sub   { color:var(--muted); font-size:14px; margin-bottom:24px; }
  .fields     { display:flex; flex-direction:column; gap:14px; margin-bottom:24px; }
  .slug-preview { margin-top:6px; font-size:12px; color:var(--muted); }
  .slug-preview span { color:var(--teal); font-weight:500; }
  .slug-ok    { color:var(--success); font-size:12px; margin-top:4px; }
  .slug-taken { color:var(--danger);  font-size:12px; margin-top:4px; }
  .btn-row    { display:flex; gap:10px; }
  .btn-row .btn-primary, .btn-row .btn-secondary { flex:1; justify-content:center; }
  .auth-footer { text-align:center; margin-top:18px; color:var(--muted); font-size:14px; }
  .auth-footer a { color:var(--blue); font-weight:500; }
  .auth-error  { color:var(--danger); font-size:13px; margin-bottom:12px; text-align:center; }
  .success-screen { text-align:center; padding:20px 0; }
  .success-screen .big-icon { font-size:64px; margin-bottom:20px; animation:pop .4s cubic-bezier(.175,.885,.32,1.275); }
  .success-screen h2 { font-family:var(--head); font-size:32px; font-weight:800; letter-spacing:2px; margin-bottom:10px; }
  .success-screen p  { color:var(--muted); font-size:15px; margin-bottom:28px; line-height:1.6; }
`;

function toSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 40);
}

export default function Signup() {
  const [step, setStep]         = useState(1);
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [bizName, setBizName]   = useState("");
  const [slug, setSlug]         = useState("");
  const [phone, setPhone]       = useState("");
  const [city, setCity]         = useState("");
  const [state, setState]       = useState("");
  const [slugStatus, setSlugStatus] = useState(null); // null | "ok" | "taken" | "checking"
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [done, setDone]         = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [resent, setResent]     = useState(false);

  const handleBizName = val => {
    setBizName(val);
    const s = toSlug(val);
    setSlug(s);
    setSlugStatus(null);
  };

  const checkSlug = async val => {
    const s = toSlug(val);
    setSlug(s);
    if (!s) { setSlugStatus(null); return; }
    setSlugStatus("checking");
    const { data } = await supabase.from("detailers").select("id").eq("slug", s).maybeSingle();
    setSlugStatus(data ? "taken" : "ok");
  };

  const pwStrength = pw => {
    if (!pw) return null;
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    if (s <= 1) return { w:"25%", c:"var(--danger)", l:"Weak" };
    if (s === 2) return { w:"50%", c:"#f59e0b", l:"Fair" };
    if (s === 3) return { w:"75%", c:"var(--teal)", l:"Good" };
    return { w:"100%", c:"#22c55e", l:"Strong" };
  };

  const createAccount = async e => {
    e.preventDefault();
    if (!tosAccepted) { setError("Please accept the Terms of Service and Privacy Policy to continue."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError(null);
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }
    // Try signing in immediately — works when email confirm is disabled
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
    if (!signInErr) { setStep(2); setLoading(false); return; }
    // Email confirmation required — show verify screen
    const msg = signInErr.message?.toLowerCase() || "";
    if (msg.includes("not confirmed") || msg.includes("confirm") || msg.includes("verify")) {
      setVerifyEmail(true); setLoading(false); return;
    }
    setError(signInErr.message);
    setLoading(false);
  };

  const retryLogin = async () => {
    setLoading(true); setVerifyError(null);
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
    if (!signInErr) { setVerifyEmail(false); setStep(2); setLoading(false); return; }
    const msg = signInErr.message?.toLowerCase() || "";
    if (msg.includes("not confirmed") || msg.includes("confirm") || msg.includes("verify")) {
      setVerifyError("Email not yet verified. Check your inbox and click the link first.");
    } else {
      setVerifyError(signInErr.message);
    }
    setLoading(false);
  };

  const resendVerification = async () => {
    await supabase.auth.resend({ type: "signup", email });
    setResent(true);
  };

  const finish = async e => {
    e.preventDefault();
    if (slugStatus === "taken") { setError("That URL is already taken. Please choose another."); return; }
    setLoading(true); setError(null);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setError("Session expired. Please sign in again."); setLoading(false); return; }

    // Create detailer profile
    const { data: detailer, error: dErr } = await supabase
      .from("detailers")
      .insert([{ user_id: user.id, business_name: bizName, slug, phone, email: user.email, city, state }])
      .select().single();

    if (dErr) { setError(dErr.message); setLoading(false); return; }

    // Seed default services + pricing
    for (let i = 0; i < DEFAULT_SERVICES.length; i++) {
      const svc = DEFAULT_SERVICES[i];
      const { data: svcRow } = await supabase
        .from("services")
        .insert([{ detailer_id: detailer.id, name: svc.name, description: svc.description, icon: svc.icon, duration: svc.duration, sort_order: i }])
        .select().single();
      if (svcRow) {
        const pricing = Object.entries(svc.pricing).map(([vehicle_type, price]) => ({ service_id: svcRow.id, vehicle_type, price }));
        await supabase.from("service_pricing").insert(pricing);
      }
    }

    setDone(true);
    setLoading(false);
    // Redirect to dashboard after short delay
    setTimeout(() => { window.location.href = "/dashboard"; }, 2000);
  };

  const StepBar = () => (
    <div className="step-bar">
      {[1,2].map((s, i) => (
        <span key={s} style={{ display:"flex", alignItems:"center", flex: i < 1 ? "none" : 1 }}>
          <div className={`step-pip ${step > s ? "done" : step === s ? "active" : ""}`}>
            {step > s ? "✓" : s}
          </div>
          {i < 1 && <div className={`step-line ${step > 1 ? "done" : ""}`} />}
        </span>
      ))}
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <div className="signup-page">
        <div className="signup-card fade-in">
          <div className="signup-logo">NEO<span> DETAIL</span></div>

          {verifyEmail ? (
            <div className="success-screen">
              <div className="big-icon">📧</div>
              <h2>CHECK YOUR <span style={{ color:"var(--teal)" }}>EMAIL</span></h2>
              <p>
                We sent a verification link to{" "}
                <strong style={{ color:"var(--teal)" }}>{email}</strong>.
                <br />Click it to confirm your address, then come back here.
              </p>
              {verifyError && <div className="auth-error">{verifyError}</div>}
              {resent && <div style={{ color:"var(--teal)", fontSize:13, marginBottom:12 }}>✓ Verification email resent!</div>}
              <button className="btn-primary full" onClick={retryLogin} disabled={loading} style={{ marginBottom:12 }}>
                {loading ? <><span className="spinner" /> Checking...</> : "I've Verified — Continue →"}
              </button>
              <button
                style={{ background:"none", border:"none", color:"var(--muted)", fontSize:13, cursor:"pointer", width:"100%" }}
                onClick={resendVerification}
                disabled={resent}
              >
                {resent ? "Email sent!" : "Didn't get it? Resend email"}
              </button>
            </div>
          ) : done ? (
            <>
              <StepBar />
              {/* STEP 3 — Your page is live */}
              <div className="step-title">YOU'RE <span style={{ color:"var(--teal)" }}>LIVE!</span></div>
              <div className="step-sub">Your booking page is ready. Here's how to get your first job.</div>

              {/* Booking link */}
              <div style={{ background:"var(--card2)", border:"1px solid var(--teal)", borderRadius:12, padding:16, marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Your Booking Page</div>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ color:"var(--teal)", fontWeight:600, fontSize:14, flex:1, wordBreak:"break-all" }}>
                    neodetail.app/book/{slug}
                  </span>
                  <button className="btn-edit" style={{ fontSize:12, flexShrink:0 }}
                    onClick={() => navigator.clipboard.writeText(`https://neodetail.app/book/${slug}`)}>
                    Copy Link
                  </button>
                  <a href={`/book/${slug}`} target="_blank" rel="noreferrer">
                    <button className="btn-edit" style={{ fontSize:12 }}>View →</button>
                  </a>
                </div>
              </div>

              {/* Next steps checklist */}
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                {[
                  { icon:"📸", label:"Add your photo to Instagram bio with your booking link" },
                  { icon:"🛠",  label:"Customize your services and prices in the dashboard" },
                  { icon:"⚙️", label:"Add your city so customers know your service area" },
                  { icon:"💳", label:"Connect Stripe (Pro) to collect deposits and stop no-shows" },
                ].map(({ icon, label }) => (
                  <div key={label} style={{ display:"flex", alignItems:"flex-start", gap:10, background:"var(--card2)", border:"1px solid var(--border)", borderRadius:10, padding:"11px 14px" }}>
                    <span style={{ fontSize:18, flexShrink:0 }}>{icon}</span>
                    <span style={{ fontSize:13, color:"var(--muted)", lineHeight:1.5 }}>{label}</span>
                  </div>
                ))}
              </div>

              <button className="btn-primary full" onClick={() => { window.location.href = "/dashboard"; }}>
                Go to Dashboard →
              </button>
            </>
          ) : (
            <>
              <StepBar />

              {step === 1 && (
                <>
                  <div className="step-title">CREATE ACCOUNT</div>
                  <div className="step-sub">Free forever. No credit card required.</div>
                  <form onSubmit={createAccount}>
                    <div className="fields">
                      <div className="form-group">
                        <label>Email</label>
                        <input type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)} minLength={8} required />
                        {password && (() => { const s = pwStrength(password); return s ? (
                          <>
                            <div style={{ height:4, borderRadius:2, marginTop:6, background:s.c, width:s.w, transition:"all .3s" }} />
                            <div style={{ fontSize:11, color:s.c, marginTop:3 }}>{s.l}</div>
                          </>
                        ) : null; })()}
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:16 }}>
                      <input type="checkbox" id="tos" checked={tosAccepted} onChange={e => setTosAccepted(e.target.checked)}
                        style={{ marginTop:2, flexShrink:0, accentColor:"var(--blue)", width:16, height:16, cursor:"pointer" }} />
                      <label htmlFor="tos" style={{ fontSize:13, color:"var(--muted)", lineHeight:1.5, cursor:"pointer" }}>
                        I agree to the{" "}
                        <Link to="/terms" target="_blank" style={{ color:"var(--blue)", fontWeight:500 }}>Terms of Service</Link>
                        {" "}and{" "}
                        <Link to="/privacy" target="_blank" style={{ color:"var(--blue)", fontWeight:500 }}>Privacy Policy</Link>
                      </label>
                    </div>
                    {error && <div className="auth-error">{error}</div>}
                    <button className="btn-primary full" type="submit" disabled={loading || !tosAccepted}>
                      {loading ? <><span className="spinner" /> Creating account...</> : "Continue →"}
                    </button>
                  </form>
                  <div className="auth-footer">Already have an account? <Link to="/login">Sign in</Link></div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="step-title">YOUR BUSINESS</div>
                  <div className="step-sub">This is how customers will find your booking page.</div>
                  <form onSubmit={finish}>
                    <div className="fields">
                      <div className="form-group">
                        <label>Business Name</label>
                        <input type="text" placeholder="e.g. Jay's Auto Detailing" value={bizName}
                          onChange={e => handleBizName(e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <label>Your Booking URL</label>
                        <input type="text" placeholder="jays-auto-detailing" value={slug}
                          onChange={e => checkSlug(e.target.value)} required />
                        <div className="slug-preview">neodetail.app/book/<span>{slug || "your-name"}</span></div>
                        {slugStatus === "ok"       && <div className="slug-ok">✓ Available</div>}
                        {slugStatus === "taken"    && <div className="slug-taken">✕ Already taken — try another</div>}
                        {slugStatus === "checking" && <div className="slug-preview">Checking...</div>}
                      </div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input type="tel" placeholder="(555) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} />
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                        <div className="form-group">
                          <label>City</label>
                          <input type="text" placeholder="Miami" value={city} onChange={e => setCity(e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label>State</label>
                          <input type="text" placeholder="FL" value={state} onChange={e => setState(e.target.value)} maxLength={2} />
                        </div>
                      </div>
                    </div>
                    {error && <div className="auth-error">{error}</div>}
                    <div className="btn-row">
                      <button type="button" className="btn-secondary" onClick={() => { setStep(1); setError(null); }}>← Back</button>
                      <button className="btn-primary" type="submit" disabled={loading || slugStatus === "taken" || !bizName || !slug}>
                        {loading ? <><span className="spinner" /> Setting up...</> : "Launch My Page →"}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
